import React, { useCallback, useMemo } from 'react';

import { TopLevelSpacer } from '@atlaskit/navigation-system/side-nav-items/top-level-spacer';

import GlobalShortcuts, {
	type GlobalShortcutsProps,
	productLinksWorkspaceCloudIdFilter,
	useOrderedAppShortcuts,
} from '@atlassian/global-shortcuts';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';

import { useShortcutItemsConfig } from './hooks/useShortcutItemsConfig.tsx';
import { useTeamsAppShortcut } from './hooks/useTeamsAppShortcut.tsx';

type GlobalAppShortcutsProps = {
	orderedMenuConfig?: { menuId: string; visible: boolean }[];
	onUpdateGetFilteredProductLinks?: GlobalShortcutsProps['onUpdateGetFilteredProductLinks'];
	isInvitedAppShortcutsEnabled?: boolean;
};

export const GlobalAppShortcuts = ({
	orderedMenuConfig = [],
	onUpdateGetFilteredProductLinks,
	isInvitedAppShortcutsEnabled = false,
}: GlobalAppShortcutsProps) => {
	const { cloudId, orgId } = useSessionData();
	const { config: teamsShortcutConfig, component: teamsShortcutComponent } = useTeamsAppShortcut();

	const updateGetFilteredProductLinks = useCallback(
		(getFilteredProductLinks) => {
			if (onUpdateGetFilteredProductLinks) {
				onUpdateGetFilteredProductLinks(getFilteredProductLinks);
			}
		},
		[onUpdateGetFilteredProductLinks],
	);

	const shortcutItemsConfig = useShortcutItemsConfig({ teamsShortcutConfig });

	const shortcutItemsConfigReduced = useMemo(() => {
		if (!isInvitedAppShortcutsEnabled) {
			return shortcutItemsConfig;
		}

		const { jiraShortcut: _jiraShortcut, ...rest } = shortcutItemsConfig;

		return rest;
	}, [shortcutItemsConfig, isInvitedAppShortcutsEnabled]);

	// Sort shortcut items based on the ordered menu config
	const orderedShortcuts = useOrderedAppShortcuts(shortcutItemsConfigReduced, orderedMenuConfig);

	const productLinksFilter = cloudId ? productLinksWorkspaceCloudIdFilter(cloudId) : undefined;

	return (
		<>
			<GlobalShortcuts
				testId="global-app-shortcuts"
				cloudId={cloudId}
				orgId={orgId}
				shortcutItems={orderedShortcuts}
				promoteSite
				product="confluence"
				productLinksFilter={productLinksFilter}
				onUpdateGetFilteredProductLinks={updateGetFilteredProductLinks}
			/>
			{orderedShortcuts.length > 0 && <TopLevelSpacer />}
			{fg('teams-shortcut-next-confluence') && teamsShortcutComponent}
		</>
	);
};
